import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { API } from "common/utils";
import { RootState } from "model";

export type AccessibleUser = {
  guid: string;
  name?: string;
  mail: string;
};

const accessibleUsersApi = new API<AccessibleUser>("accessibleUser");

const accessibleUsersAdapter = createEntityAdapter<AccessibleUser>({
  selectId: (accessibleUser) => accessibleUser.guid,
});

export const fetchAccessibleUserById = createAsyncThunk(
  "accessibleUsers/fetchById",
  async (accessibleUserId: string) => {
    const fetchedUser = await accessibleUsersApi.getOne(accessibleUserId);
    return fetchedUser;
  }
);

export const {
  selectAll: selectAllAccessibleUsers,
  selectById: selectAccessibleUserById,
  selectEntities: selectAccessibleUsersEntities,
  selectIds: selectAccessibleUserIds,
} = accessibleUsersAdapter.getSelectors(
  (state: RootState) => state.accessibleUsers
);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";

export type AccessibleUsersState = EntityState<AccessibleUser> & {
  loading: LoadingState;
  error: null;
  status: Status;
};

const initialAccessibleUsersState: AccessibleUsersState =
  accessibleUsersAdapter.getInitialState({
    loading: "idle",
    error: null,
    status: null,
  });

const accessibleUsersSlice = createSlice({
  name: "accessibleUsers",
  initialState: initialAccessibleUsersState,
  reducers: {
    resetAccessibleUsers: () => initialAccessibleUsersState,
  },
  extraReducers: {
    [fetchAccessibleUserById.fulfilled.type]: (state, { payload }) => {
      accessibleUsersAdapter.upsertOne(state, payload as AccessibleUser);
    },
  },
});

const { actions, reducer } = accessibleUsersSlice;

export const { resetAccessibleUsers } = actions;
export const accessibleUserReducer = reducer;
